import { useEffect } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js"
import { trpc } from "@/lib/trpc-client"
import { CLIENT_ID } from "@/lib/client-id"

export const tripKey = (tripId: string) => [`trip`, tripId] as const

// The version-history list (useUndoRedo). Lives here so the trip/guide
// invalidation paths can refresh it without importing useUndoRedo (which would
// be a cycle — useUndoRedo imports this module).
export const versionsKey = (entityType: `trip` | `guide`, entityId?: string) =>
  [`versions`, entityType, entityId] as const

export type TripData = Awaited<ReturnType<typeof trpc.trips.getTripData.query>>

// Subscribe to the trip's Realtime Broadcast topic. Statement-level Postgres
// triggers (notify_trip_change_stmt) emit one `changed` signal per affected
// trip whenever a trip table is written, with the originating client's id in
// the payload. We ignore our own echo — the local mutation flow already patched
// optimistically and explicitly invalidates — so only a *collaborator's* change
// drives a refetch here. The small debounce coalesces the few signals a
// multi-statement transaction (e.g. note + item) produces. The id is an opaque
// nonce and the refetch goes through authed tRPC, so the public channel leaks
// nothing.
function useTripBroadcast(tripId: string) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!tripId) return
    let timer: ReturnType<typeof setTimeout> | null = null

    const onChanged = (message: { payload?: { client_id?: string } }) => {
      // Our own write's echo — skip; the mutation flow handles reconciliation.
      if (message.payload?.client_id === CLIENT_ID) return
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: tripKey(tripId) })
        queryClient.invalidateQueries({ queryKey: versionsKey(`trip`, tripId) })
      }, 250)
    }

    // Lazy-import the Supabase client so its ~203 KB bundle stays out of the
    // initial JS wave: the planner is JS-heavy and realtime is a post-load
    // concern, while route-preloading (viewport) of trip links pulled this
    // module — and therefore Supabase — onto marketing pages. Subscribing one
    // microtask later is imperceptible for a "something changed" signal.
    let channel: RealtimeChannel | undefined
    let client: SupabaseClient | undefined
    let removed = false
    void import(`@/lib/supabase-browser`).then(({ supabase }) => {
      if (removed) return
      client = supabase
      channel = supabase
        .channel(`trip:${tripId}`)
        .on(`broadcast`, { event: `changed` }, onChanged)
        .subscribe()
    })

    return () => {
      removed = true
      if (timer) clearTimeout(timer)
      if (client && channel) client.removeChannel(channel)
    }
  }, [tripId, queryClient])
}

export function useTripData(tripId: string) {
  useTripBroadcast(tripId)

  return useQuery({
    queryKey: tripKey(tripId),
    queryFn: () => trpc.trips.getTripData.query({ id: tripId }),
    enabled: !!tripId,
  })
}

// Drop this in any component that mutates trip data. Call after the tRPC
// mutation resolves to pull fresh data and update all subscribers.
export function useInvalidateTripData() {
  const queryClient = useQueryClient()
  return (tripId: string) => {
    queryClient.invalidateQueries({ queryKey: tripKey(tripId) })
    // Keep the undo/redo history list in sync — mutations create new versions.
    queryClient.invalidateQueries({ queryKey: versionsKey(`trip`, tripId) })
  }
}

// Optimistic updates against the cached getTripData. Apply the change to the
// cache immediately for instant UI, fire the tRPC mutation, then invalidate to
// reconcile with the server.
export function useTripDataMutators(tripId: string) {
  const queryClient = useQueryClient()

  const optimisticUpdate = (updater: (data: TripData) => TripData) => {
    queryClient.setQueryData<TripData>(tripKey(tripId), (old) =>
      old ? updater(old) : old
    )
  }

  return { optimisticUpdate }
}
